import React, { useEffect, useState } from 'react';
import { adminAnalytics } from '../../lib/adminApi';
import { BarChart3, Globe, Smartphone, Search, Package, Loader2 } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-5">
    <div className="flex items-center justify-between mb-3">
      <p className="text-zinc-500 text-xs uppercase tracking-wider">{label}</p>
      <Icon className="text-[#909090]" size={20} />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const TopList = ({ icon: Icon, title, items, emptyText, formatLabel }) => (
  <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="text-[#909090]" size={18} />
      <h3 className="font-semibold text-white">{title}</h3>
    </div>
    {items.length === 0 ? (
      <p className="text-zinc-500 text-sm py-4">{emptyText}</p>
    ) : (
      <ol className="space-y-2">
        {items.map((it, i) => (
          <li key={`${it.label}-${i}`} className="flex items-center justify-between text-sm">
            <span className="text-zinc-300 truncate flex items-center gap-2">
              <span className="text-zinc-600 w-5 text-right">{i + 1}.</span>
              {formatLabel ? formatLabel(it.label) : it.label}
            </span>
            <span className="text-[#909090] font-semibold tabular-nums">{it.count}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminAnalytics(days)
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem('hh_admin_token');
          window.location.reload();
        }
      })
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BarChart3 className="text-[#909090]" size={26}/>
            <h1 className="text-3xl font-bold">Analytics</h1>
          </div>
          <p className="text-zinc-500 text-sm">Visitor & search insights (bots excluded)</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-zinc-950 border border-zinc-800 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-[#909090]"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {loading || !data ? (
        <div className="py-20 text-center text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={18}/> Loading...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={BarChart3} label="Total events" value={data.totals.events}/>
            <StatCard icon={Globe} label="Page views" value={data.totals.page_views}/>
            <StatCard icon={Package} label="Product views" value={data.totals.product_views}/>
            <StatCard icon={Search} label="Searches" value={data.totals.searches}/>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <TopList icon={Globe} title="Visitors by country" items={data.by_country} emptyText="No visitors yet." />
            <TopList icon={Smartphone} title="Visitors by platform" items={data.by_platform} emptyText="No visitors yet." />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <TopList icon={Package} title="Top 10 products viewed" items={data.top_products} emptyText="No product views yet." />
            <TopList icon={Search} title="Top 10 searches" items={data.top_searches} emptyText="No searches yet." />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <TopList icon={BarChart3} title="Top pages" items={data.top_pages} emptyText="No page views yet." />
            <TopList icon={Smartphone} title="Browser breakdown" items={data.by_browser} emptyText="No data yet." />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
